/**
 * Protected Route Component
 *
 * Wraps routes that require authentication and/or specific permissions.
 * Redirects to home page if user doesn't have access.
 */

import React, { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface ProtectedRouteProps {
  children: ReactNode;
  /** If true, only checks authentication, not page-specific permissions */
  authOnly?: boolean;
  /** If true, requires admin role */
  adminOnly?: boolean;
  /** Custom path to check access for (defaults to current location) */
  requiredPath?: string;
  /** Custom redirect path if access denied */
  redirectTo?: string;
}

/**
 * ProtectedRoute component
 *
 * Usage:
 * ```tsx
 * // Check page-level permissions
 * <Route path="/admin" element={
 *   <ProtectedRoute>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 *
 * // Admin only
 * <Route path="/admin" element={
 *   <ProtectedRoute adminOnly>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 *
 * // Auth only (any logged in user)
 * <Route path="/profile" element={
 *   <ProtectedRoute authOnly>
 *     <Profile />
 *   </ProtectedRoute>
 * } />
 * ```
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  authOnly = false,
  adminOnly = false,
  requiredPath,
  redirectTo = "/",
}) => {
  const { user, hasPageAccess, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  // Show nothing while loading (prevents flash)
  if (isLoading) {
    return null;
  }

  // Check if user is authenticated
  if (!user) {
    // User not logged in - they'll see the login screen
    return null;
  }

  // If authOnly, just check authentication
  if (authOnly) {
    return <>{children}</>;
  }

  // If adminOnly, check admin role
  if (adminOnly && !isAdmin) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check page-specific permissions
  const pathToCheck = requiredPath || location.pathname;

  if (!hasPageAccess(pathToCheck)) {
    // User doesn't have access to this page
    return <Navigate to={redirectTo} replace />;
  }

  // User has access
  return <>{children}</>;
};

/**
 * Access Denied component for inline usage
 */
export const AccessDenied: React.FC<{ message?: string }> = ({
  message = "You don't have permission to access this page.",
}) => {
  return (
    <div className="access-denied">
      <div className="access-denied-content">
        <h2>Access Denied</h2>
        <p>{message}</p>
        <a href="/" className="btn btn-primary">
          Go to Dashboard
        </a>
      </div>
    </div>
  );
};

export default ProtectedRoute;

